import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import useAuthStore from "../../store/authStore";
import AuthPageLayout from "../../components/authPageLayout/AuthPageLayout";

interface IFormInput {
  email: string;
  password: string;
}

const Login = () => {
  const [message, setMessage] = useState<string | null>(null);
  const [currentState, setCurrentState] = useState<
    "login" | "reset" | "successfulReset"
  >("login");
  const navigate = useNavigate();
  const {
    loginUser,
    resetPassword,
    isBusyAuthenticating,
    isResetSuccess,
    setIsResetSuccess,
  } = useAuthStore();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormInput>();

  const onLogin: SubmitHandler<IFormInput> = async (data) => {
    const res = await loginUser(data);
    if (res.code === 1004) {
      navigate("/");
    }
    setMessage(res.message);
  };

  const onForgotPassword: SubmitHandler<IFormInput> = async (data) => {
    const email = data.email;
    await resetPassword(email);
  };

  const handleOkClick = () => {
    setCurrentState("login");
    setIsResetSuccess(false);
    reset();
  };

  useEffect(() => {
    if (isResetSuccess) {
      setCurrentState("successfulReset");
    }
  }, [isResetSuccess]);

  const ResetSuccessView = () => {
    return (
      <div className="text-gray-500 tracking-normal font-normal text-center text-sm leading-7">
        <p>
          If it is a valid email and belongs to a registered user, an email with
          reset link has been sent. Please follow the instructions in the email
          to reset your password.
        </p>
        <button
          onClick={handleOkClick}
          className="flex w-full mt-6 justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600"
        >
          Ok
        </button>
      </div>
    );
  };

  return (
    <AuthPageLayout isBusy={isBusyAuthenticating}>
      <div className="relative flex flex-col items-center w-full max-w-md p-4 mx-auto mb-8 flex-grow">
        <div className="flex-grow flex flex-col w-full overflow-hidden">
          <div className="flex-grow flex flex-col overflow-y-auto max-h-[calc(100vh-8rem)]">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-white dark:text-gray-200 mb-4 md:text-3xl">
                Sign in
              </h1>
              <p className="text-base text-gray-600 dark:text-gray-200 mt-2 md:mt-4 md:text-lg">
                <>
                  Join the League and swing into action
                  <br className="hidden md:inline" />
                  Your golf journey starts here!
                </>
              </p>
            </div>

            <div className="w-full max-w-md bg-white bg-opacity-80 p-8 rounded-lg shadow-lg mt-8 dark:bg-gray-800">
              {currentState === "successfulReset" ? (
              <ResetSuccessView />
              ) : (
              <form
                onSubmit={handleSubmit(
                currentState === "reset" ? onForgotPassword : onLogin
                )}
                className="space-y-6"
              >
                <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-500 dark:text-gray-300"
                >
                  Email address
                </label>
                <div className="mt-1">
                  <input
                  id="email"
                  {...register("email", {
                    required: "Email is required",
                  })}
                  type="email"
                  autoComplete="email"
                  className="block w-full p-2 rounded-md border border-gray-300 bg-white text-gray-900 dark:text-gray-100 dark:bg-gray-700 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  {errors.email && (
                  <span className="text-red-500">
                    {errors.email.message}
                  </span>
                  )}
                </div>
                </div>

                {currentState === "login" && (
                <>
                  <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                  >
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                    id="password"
                    {...register("password", {
                      required: "Password is required",
                    })}
                    type="password"
                    autoComplete="current-password"
                    className="block w-full p-2 rounded-md border border-gray-300 bg-white text-gray-900 dark:text-gray-100 dark:bg-gray-700 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    {errors.password && (
                    <span className="text-red-500">
                      {errors.password.message}
                    </span>
                    )}
                  </div>
                  </div>

                  <div className="text-red-500 text-center">
                  {message && <span>{message}</span>}
                  </div>
                </>
                )}

                <div className="text-sm text-gray-400 leading-7 tracking-normal dark:text-gray-300 font-normal text-center">
                {currentState === "reset" && (
                  <p className="text-gray-400 font-normal dark:text-gray-400 mb-4">
                  An email with reset link will be sent to this email
                  address. <br /> This should be a valid email address and
                  should match the one used during registration
                  </p>
                )}

                <button
                  type="submit"
                  className="flex w-full mt-4 justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600"
                >
                  {currentState === "reset" ? "Send reset link" : "Sign in"}
                </button>
                </div>
                {currentState === "login" && (
                <div className="text-sm text-center">
                  <p className="text-blue-500 cursor-pointer font-medium">
                  <span
                    onClick={() => {
                    setCurrentState("reset");
                    reset();
                    }}
                  >
                    I forgot my password!
                  </span>
                  </p>
                </div>
                )}
              </form>
              )}

              <p className="mt-4 text-center text-sm text-gray-500">
              Not a member?{" "}
              <span
                onClick={() => navigate("/register")}
                className="font-semibold text-indigo-600 hover:text-indigo-500 cursor-pointer"
              >
                Register
              </span>
              </p>
              <p className="mt-4 text-center text-sm text-gray-500">
              <span
                onClick={() => navigate("/")}
                className="font-semibold text-indigo-600 hover:text-indigo-500 cursor-pointer"
              >
                Cancel
              </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
};

export default Login;
